"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { X, Search, MapPin } from "lucide-react";
import { fetchAPI, fetchListingsLocations, fetchResidenceNames, type ResidenceName } from "@/lib/api";
import { useLocale, useT } from "@/lib/locale-context";
import { useSheetDrag } from "@/lib/use-sheet-drag";
import { useBackToClose } from "@/lib/use-back-to-close";
import { isPhoneWidth, prefersReducedMotion } from "@/lib/motion";

// The sheets' one motion (components/BottomSheet, DESIGN.md): 200 ms,
// ease-out, from below on a phone, a soft grow on a desktop, a plain
// cross-fade with Reduce Motion.
const SHEET_MS = 200;
import SheetGrip from "@/components/SheetGrip";
import type { LocationGroup, LocationNode } from "@/types";
import type { ReadonlyURLSearchParams } from "next/navigation";
import { showLabel, useLiveCount, withPatch } from "@/lib/listings-count";
import { FILTER_CHECK, FILTER_COUNT, ROW_ON, SELECTED, UNSELECTED } from "@/lib/ui-classes";

interface Props {
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  /** When provided, the modal gains a "Kompleks" tab: residences from
   *  /residence-names.json, multi-selected and committed as
   *  complex_slug params - same staging/Apply model as locations. */
  selectedComplexSlugs?: string[];
  onComplexChange?: (slugs: string[]) => void;
  /** Atomic commit of BOTH lists in one call. REQUIRED for callers
   *  whose handlers merge into the URL: calling onChange then
   *  onComplexChange fires two merges where the second reads stale
   *  params and wipes the first (the "picking a residence cleared my
   *  district" bug). When set, Apply/clear use this exclusively. */
  onApply?: (ids: string[], complexSlugs: string[], streets?: string[]) => void;
  /** Streets (services/streets): found by typing in the search box,
   *  committed as repeatable `street` params. */
  selectedStreets?: string[];
  onStreetsChange?: (ids: string[]) => void;
  /** A radius in the URL (drawn on /map) has no control of its own on
   *  /listings: the trigger shows it and the picker's reset / clear
   *  removes it. Applying any selection replaces it (see
   *  lib/place-vs-radius). */
  radiusKm?: number | null;
  /** Areas drawn on /map (`?draw=`, lib/draw-area): shown in the trigger
   *  like the radius, cleared by the same reset. */
  drawnAreas?: number;
  /** The rest of the search the picks join: when set, the bottom button
   *  is the live "N elanı göstərin" for it with the staged places. */
  countBase?: URLSearchParams | ReadonlyURLSearchParams;
}

/** One street from GET /api/streets/suggest (or /streets/{id}). */
interface StreetHit { id: number; name: string; place?: string | null; district?: string | null; listings_n?: number }
const streetLabel = (h: StreetHit) => (h.place ? `${h.name} · ${h.place}` : h.name);

type ResidenceRow = ResidenceName;

/** AZ-diacritic fold so "qelebe" matches "Qələbə". */
/** The query without a trailing/leading residence designator, folded:
 *  "park yasamal residence" -> "park yasamal"; "" when it was only the
 *  designator (never match everything on "residence" alone). */
const RESIDENCE_WORD_RE = /(?:^|\s)(?:residence|residences|rezidens|rezidensi|rezidensiya|rezidensiyasi|rezidans|mtk|yk|jk|zhk|kompleks|kompleksi|yasayis kompleksi|complex|towers?|plaza)(?=\s|$)/g;
function stripResidenceWord(q: string): string {
  return q.replace(RESIDENCE_WORD_RE, " ").replace(/\s+/g, " ").trim();
}

function foldAz(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ə/g, "e")
    .replace(/ı/g, "i");
}

const TAB_KEYS: Record<string, "listings.location.tab.rayon" | "listings.location.tab.metro" | "listings.location.tab.qesebe" | "listings.location.tab.nisangah" | "listings.location.tab.complex"> = {
  rayon: "listings.location.tab.rayon",
  metro: "listings.location.tab.metro",
  qesebe: "listings.location.tab.qesebe",
  nisangah: "listings.location.tab.nisangah",
  complex: "listings.location.tab.complex",
};

// One selectable row - shared between the plain tab grids, the grouped
// Qəsəbə view and the cross-tab search results. `hint` renders a small
// right-aligned type/context label (used by search results so "Yasamal"
// the rayon and "Yasamal" the settlement are tellable apart).
function LocationRow({
  loc,
  checked,
  onToggle,
  hint,
}: {
  loc: LocationNode;
  checked: boolean;
  onToggle: () => void;
  hint?: string;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
        checked ? ROW_ON : "text-gray-700 hover:bg-gray-50"
      }`}
    >
      <span
        className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${
          checked ? FILTER_CHECK : "border-gray-300"
        }`}
      >
        {checked && (
          <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 10 8" fill="none">
            <path d="M1 4l3 3 5-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </span>
      <span className="text-left truncate flex-1">{loc.name}</span>
      {hint && (
        <span className={`text-[10px] shrink-0 ${checked ? "text-gray-600" : "text-gray-400"}`}>
          {hint}
        </span>
      )}
    </button>
  );
}

/**
 * bina.az-style location picker rendered as a MODAL (not an inline
 * dropdown). The trigger shows the selected count; the modal has tabs
 * (Rayon / Metro / Qəsəbə / Nişangah), a search box, multi-select, and
 * Apply / Reset. Selection is staged in a local draft and only committed
 * to the URL (via onChange) when the user hits Apply - so a half-made
 * selection never re-runs the search.
 */
export default function ListingsLocationPicker({
  selectedIds, onChange, selectedComplexSlugs, onComplexChange, onApply, radiusKm, selectedStreets, onStreetsChange, drawnAreas, countBase,
}: Props) {
  const t = useT();
  const { locale } = useLocale();
  const [open, setOpen] = useState(false);
  const [groups, setGroups] = useState<LocationGroup[]>([]);
  const [activeTab, setActiveTab] = useState("rayon");
  const [query, setQuery] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  // Draft selection - committed to onChange only on Apply.
  const [draft, setDraft] = useState<string[]>(selectedIds);
  const [mounted, setMounted] = useState(false);
  // Residence (Kompleks) tab - only active when the caller wired the
  // complex handlers.
  const complexEnabled = !!onComplexChange;
  const [residences, setResidences] = useState<ResidenceRow[]>([]);
  const [complexDraft, setComplexDraft] = useState<string[]>(selectedComplexSlugs ?? []);
  // Streets: searched on the server as you type (no list to browse,
  // 6k of them), names cached so chips and the trigger can label them.
  const streetsEnabled = !!onStreetsChange || !!onApply;
  const [streetDraft, setStreetDraft] = useState<string[]>(selectedStreets ?? []);
  const [streetHits, setStreetHits] = useState<StreetHit[]>([]);
  const [streetNames, setStreetNames] = useState<Record<string, string>>({});

  useEffect(() => setMounted(true), []);

  // Load taxonomy once on mount.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchListingsLocations()
      .then((res) => {
        if (!cancelled) {
          setGroups(res.groups);
          if (res.groups.length > 0) setActiveTab(res.groups[0].type);
        }
      })
      .catch(() => { /* empty groups - graceful */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Residences load lazily alongside the taxonomy when the tab exists.
  useEffect(() => {
    if (!complexEnabled) return;
    let cancelled = false;
    fetchResidenceNames()
      .then((res) => {
        if (cancelled) return;
        setResidences(res.residences ?? []);
      })
      .catch(() => { /* tab just renders empty */ });
    return () => { cancelled = true; };
  }, [complexEnabled]);

  // One rule for every sheet on a phone (2026-10-29): closing it (a swipe,
  // a tap outside, Back) applies what is staged, like its bottom button.
  const closeApplies = useRef<() => void>(() => setOpen(false));
  const closeModal = useCallback(() => closeApplies.current(), []);

  // Mobile bottom-sheet drag-to-close on the header (inert >= sm where
  // this renders as a centered modal).
  const { offset: dragOffset, dragging, handleProps, panelRef } = useSheetDrag(closeModal, open);
  // Back (the phone's, or Telegram's in the Mini App) closes the picker.
  useBackToClose(open, closeModal);

  // Mounted while open or sliding out; `entered` drives the transition,
  // as in BottomSheet (owner, 2026-10-29: the picker opened with no
  // motion while every other sheet slid up).
  const [render, setRender] = useState(false);
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    if (open) { setRender(true); return; }
    setEntered(false);
    const timer = setTimeout(() => setRender(false), SHEET_MS);
    return () => clearTimeout(timer);
  }, [open]);
  useEffect(() => {
    if (!render || !open) return;
    const raf = requestAnimationFrame(() => {
      // The closed position is computed before the open one, or the
      // transition has nothing to start from.
      void document.body.offsetHeight;
      setEntered(true);
    });
    return () => cancelAnimationFrame(raf);
  }, [render, open]);

  // Open: seed the draft from the committed selection. Lock body scroll.
  useEffect(() => {
    if (!open) return;
    setDraft(selectedIds);
    setComplexDraft(selectedComplexSlugs ?? []);
    setStreetDraft(selectedStreets ?? []);
    setQuery("");
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") closeModal();
    }
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", onKey);
    };
    // selectedIds intentionally re-seeds the draft each time we open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, closeModal]);

  const activeGroup: LocationGroup | undefined = groups.find((g) => g.type === activeTab);

  // Search runs ACROSS ALL TABS - with 250+ locations spread over four
  // tabs, per-tab search made finding "Masazır" from the Rayon tab
  // impossible. Matches carry their group type so the flat result list
  // can label each row (Metro / Qəsəbə / ...).
  const searching = query.trim().length > 0;
  const q = foldAz(query.trim());
  const searchResults: Array<{ loc: LocationNode; groupType: string }> = searching
    ? groups.flatMap((g) =>
        g.locations
          .filter(
            (loc) =>
              foldAz(loc.name).includes(q) ||
              (loc.full_name ? foldAz(loc.full_name).includes(q) : false) ||
              (loc.aliases ?? []).some((a) => foldAz(a).includes(q)) ||
              (loc.parent_name ? foldAz(loc.parent_name).includes(q) : false),
          )
          .map((loc) => ({ loc, groupType: g.type })),
      )
    : [];
  // Residences ride in the same cross-tab search, labelled "Kompleks".
  const residenceSearchResults: ResidenceRow[] =
    searching && complexEnabled
      // "Park Yasamal residence": the kind of place typed after the name is
      // not part of the name (the backend suggester strips it the same way).
      ? residences.filter((r) => foldAz(r.name).includes(q) || (stripResidenceWord(q) && foldAz(r.name).includes(stripResidenceWord(q))))
      : [];
  // Streets ride in the same search, from the server, debounced.
  useEffect(() => {
    if (!streetsEnabled || query.trim().length < 2) { setStreetHits([]); return; }
    let live = true;
    const timer = window.setTimeout(() => {
      fetchAPI<{ streets: StreetHit[] }>(`/streets/suggest?q=${encodeURIComponent(query.trim())}`)
        .then((d) => {
          if (!live) return;
          setStreetHits(d.streets);
          setStreetNames((cur) => ({ ...cur, ...Object.fromEntries(d.streets.map((h) => [String(h.id), streetLabel(h)])) }));
        })
        .catch(() => { if (live) setStreetHits([]); });
    }, 200);
    return () => { live = false; window.clearTimeout(timer); };
  }, [query, streetsEnabled]);
  // Names of streets committed from elsewhere (the search bar, a link).
  const committedStreetsKey = (selectedStreets ?? []).join(",");
  useEffect(() => {
    const missing = (committedStreetsKey ? committedStreetsKey.split(",") : []).filter((id) => !(id in streetNames));
    if (!missing.length) return;
    let live = true;
    Promise.all(missing.map((id) => fetchAPI<StreetHit>(`/streets/${encodeURIComponent(id)}`).then((h) => [id, streetLabel(h)] as const).catch(() => [id, ""] as const)))
      .then((pairs) => { if (live) setStreetNames((cur) => ({ ...cur, ...Object.fromEntries(pairs) })); });
    return () => { live = false; };
    // streetNames is read, not watched.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [committedStreetsKey]);

  const tabLocations: LocationNode[] = activeGroup?.locations ?? [];

  // bina-style grouping for the Qəsəbə tab: settlements render under
  // their rayon header ("Abşeron r." → Masazır, Novxanı, ...). The
  // backend pre-sorts by (parent, name) so one pass builds sections.
  const qesebeSections: Array<{ rayon: string; items: LocationNode[] }> = [];
  if (!searching && activeTab === "qesebe") {
    for (const loc of tabLocations) {
      const rayon = loc.parent_name || "";
      const last = qesebeSections[qesebeSections.length - 1];
      if (last && last.rayon === rayon) last.items.push(loc);
      else qesebeSections.push({ rayon, items: [loc] });
    }
  }

  function toggleDraft(id: string) {
    setDraft((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  }

  function toggleComplexDraft(slug: string) {
    setComplexDraft((cur) =>
      cur.includes(slug) ? cur.filter((x) => x !== slug) : [...cur, slug],
    );
  }

  function toggleStreetDraft(id: string) {
    setStreetDraft((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  }

  // ── Tab-scoped select-all ─────────────────────────────────────
  // Selecting everything then unticking a few is the natural
  // exclusion workflow; the active tab's "Hamısı" checkbox toggles
  // exactly that tab's ids (other tabs' selections are untouched).
  const activeTabIds: string[] =
    activeTab === "complex"
      ? residences.map((r) => r.cluster_id)
      : (groups.find((g) => g.type === activeTab)?.locations ?? []).map((l) => l.id);
  const activeDraft = activeTab === "complex" ? complexDraft : draft;
  const allTabSelected =
    activeTabIds.length > 0 && activeTabIds.every((id) => activeDraft.includes(id));
  const someTabSelected =
    !allTabSelected && activeTabIds.some((id) => activeDraft.includes(id));

  function toggleSelectAllTab() {
    // none/some -> select the whole tab; all -> clear the tab.
    const apply = (cur: string[]) =>
      allTabSelected
        ? cur.filter((id) => !activeTabIds.includes(id))
        : [...new Set([...cur, ...activeTabIds])];
    if (activeTab === "complex") setComplexDraft(apply);
    else setDraft(apply);
  }

  function applyAndClose() {
    if (onApply) onApply(draft, complexDraft, streetDraft);
    else {
      onChange(draft);
      onComplexChange?.(complexDraft);
      onStreetsChange?.(streetDraft);
    }
    setOpen(false);
  }

  function resetDraft() {
    setDraft([]);
    setComplexDraft([]);
    setStreetDraft([]);
    // Nothing staged to wait for: a radius-only / drawn-only reset commits at once.
    if ((radiusOnly || drawnOnly) && draft.length + complexDraft.length + streetDraft.length === 0) applyEmpty();
  }

  function applyEmpty() {
    if (onApply) onApply([], [], []);
    else { onChange([]); onComplexChange?.([]); onStreetsChange?.([]); }
    setOpen(false);
  }

  const draftCount = draft.length + complexDraft.length + streetDraft.length;
  const sameSet = (a: string[], b: string[]) => a.length === b.length && a.every((x) => b.includes(x));
  const staged = !sameSet(draft, selectedIds) || !sameSet(complexDraft, selectedComplexSlugs ?? []) || !sameSet(streetDraft, selectedStreets ?? []);
  closeApplies.current = () => {
    if (staged && isPhoneWidth()) applyAndClose();
    else setOpen(false);
  };
  // The live count of the search with the staged places (phones and
  // desktop alike, when the page passes its search).
  const liveCount = useLiveCount(open && countBase
    ? withPatch(countBase, { location_ids: draft, complex_slug: complexDraft, street: streetDraft })
    : null);

  // Build a name lookup for the trigger label.
  const nameById: Record<string, string> = {};
  groups.forEach((g) => g.locations.forEach((loc) => { nameById[loc.id] = loc.name; }));

  const committedComplexes = selectedComplexSlugs ?? [];
  const committedStreets = selectedStreets ?? [];
  residences.forEach((r) => { nameById[r.cluster_id] = r.name; });
  Object.entries(streetNames).forEach(([id, name]) => { if (name) nameById[id] = name; });
  const noPicks = selectedIds.length + committedComplexes.length + committedStreets.length === 0;
  const radiusOnly = !!radiusKm && noPicks;
  // The drawn areas (a circle and drawn areas never coexist on the map).
  const drawnOnly = !radiusOnly && !!drawnAreas && noPicks;
  const committedCount = selectedIds.length + committedComplexes.length + committedStreets.length + (radiusOnly || drawnOnly ? 1 : 0);
  const firstCommitted = selectedIds[0] ?? committedComplexes[0] ?? committedStreets[0];
  const triggerLabel = radiusOnly
    ? t("listings.filter.radius.chip").replace("{n}", String(radiusKm))
    : drawnOnly
    ? (drawnAreas === 1 ? t("listings.filter.draw.chip") : t("listings.filter.draw.chip_n").replace("{n}", String(drawnAreas)))
    : committedCount === 0
      ? t("listings.location.placeholder")
      : committedCount === 1
      ? (nameById[firstCommitted] ?? t("listings.location.selected").replace("{n}", "1"))
      : t("listings.location.selected").replace("{n}", String(committedCount));

  return (
    <>
      {/* Trigger button - shows the committed selected count. */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`w-full flex items-center justify-between gap-2 rounded-xl border px-3 py-2 text-sm transition-colors ${
          committedCount > 0 ? SELECTED : UNSELECTED
        }`}
      >
        <span className="flex items-center gap-1.5 min-w-0">
          <MapPin className="w-3.5 h-3.5 shrink-0" />
          <span className="truncate">{triggerLabel}</span>
        </span>
        {committedCount > 0 && (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              applyEmpty();
            }}
            onKeyDown={(e) => {
              if (e.key !== "Enter") return;
              e.stopPropagation();
              applyEmpty();
            }}
            className="flex items-center justify-center w-4 h-4 rounded-full hover:bg-white/20 cursor-pointer shrink-0"
            aria-label={t("listings.location.clear")}
          >
            <X className="w-3 h-3" />
          </span>
        )}
      </button>

      {/* Modal */}
      {render && mounted && createPortal(
        <div
          className="fixed inset-0 z-[1200] flex items-end sm:items-center justify-center p-0 sm:p-4"
          role="dialog"
          aria-modal="true"
          aria-label={t("listings.location.title")}
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            style={{ opacity: entered ? 1 : 0, transition: `opacity ${SHEET_MS}ms ease-out` }}
            onClick={closeModal}
          />

          {/* Panel - wide enough for a multi-column location grid
              (bina/kub-style) on desktop; full-width bottom sheet on
              mobile. */}
          <div ref={panelRef}
            style={{
              transform: entered
                ? `translateY(${dragOffset}px)`
                : prefersReducedMotion() ? "none" : isPhoneWidth() ? "translateY(100%)" : "scale(0.97)",
              opacity: entered ? 1 : prefersReducedMotion() || !isPhoneWidth() ? 0 : 1,
              transition: dragging ? "none" : `transform ${SHEET_MS}ms ease-out, opacity ${SHEET_MS}ms ease-out`,
            }}
            className="relative w-full sm:max-w-3xl max-h-[90vh] sm:max-h-[80vh] flex flex-col rounded-t-2xl sm:rounded-2xl border border-gray-200 bg-white shadow-2xl overflow-hidden"
          >
            {/* Header - doubles as the mobile drag-to-close zone. */}
            <div
              {...handleProps}
              className="flex items-center justify-between px-4 py-3 border-b border-gray-100 touch-none"
            >
              <SheetGrip className="sm:hidden" />
              <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
                {t("listings.location.title")}
              </h3>
              <button
                type="button"
                onClick={closeModal}
                className="flex items-center justify-center w-8 h-8 rounded-lg text-gray-400 hover:bg-gray-100 transition-colors max-sm:sr-only"
                aria-label={t("listings.location.close")}
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Tabs */}
            {!loading && groups.length > 0 && (
              <div className="flex border-b border-gray-100 overflow-x-auto scrollbar-hide shrink-0">
                {(() => {
                  // Kompleks rides up front (right after Rayon) - it's
                  // the product's differentiating filter, not an
                  // afterthought behind four geography tabs.
                  const types = groups.map((g) => g.type);
                  if (complexEnabled) types.splice(Math.min(1, types.length), 0, "complex");
                  return types;
                })().map((type) => {
                  const tabKey = TAB_KEYS[type] ?? "listings.location.tab.rayon";
                  // Per-tab selected count so a multi-tab selection
                  // (e.g. a rayon + two metros) is visible without
                  // clicking through every tab.
                  const draftSet = new Set(draft);
                  const tabCount =
                    type === "complex"
                      ? complexDraft.length
                      : (groups.find((g) => g.type === type)?.locations ?? [])
                          .filter((l) => draftSet.has(l.id)).length;
                  return (
                    <button
                      key={type}
                      type="button"
                      onClick={() => { setActiveTab(type); setQuery(""); }}
                      className={`flex-1 min-w-fit whitespace-nowrap px-4 py-2.5 text-sm font-medium transition-colors ${
                        activeTab === type
                          ? "text-gray-900 border-b-2 border-gray-900"
                          : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      {t(tabKey)}
                      {tabCount > 0 && (
                        <span className={`ml-1.5 inline-flex min-w-[1.1rem] items-center justify-center rounded-full px-1 py-px text-[10px] font-semibold leading-4 tabular-nums ${FILTER_COUNT}`}>
                          {tabCount}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Search */}
            <div className="p-3 border-b border-gray-100 shrink-0">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  ref={searchRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Escape" && query) { e.stopPropagation(); setQuery(""); } }}
                  placeholder={t("listings.location.search")}
                  autoFocus
                  className="w-full pl-9 pr-9 py-2 text-sm rounded-lg border border-gray-200 bg-gray-50 text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => { setQuery(""); searchRef.current?.focus(); }}
                    aria-label={t("listings.location.clear")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 flex h-6 w-6 items-center justify-center rounded-full text-gray-400 hover:bg-gray-200 hover:text-gray-700"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>

            {/* Location list - responsive multi-column grid (1-2 cols
                on mobile, up to 4 on desktop) so the long lists read
                like bina/kub instead of one tall vertical scroll.
                Three render modes: cross-tab search results (flat,
                with a type chip per row), the Qəsəbə tab grouped under
                rayon headers (bina-style), and the plain grid. */}
            <div className="flex-1 overflow-y-auto p-2">
              {/* Tab select-all - first row of the list, styled like a
                  location row. Tri-state: empty (none of this tab
                  selected), dash (some), check (all). Clicking from
                  none/some selects the whole tab; from all clears it. */}
              {!loading && !searching && activeTabIds.length > 0 && (
                <button
                  type="button"
                  onClick={toggleSelectAllTab}
                  className="inline-flex items-center gap-2.5 px-3 py-2 mb-1 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <span
                    className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${
                      allTabSelected || someTabSelected
                        ? FILTER_CHECK
                        : "border-gray-300"
                    }`}
                  >
                    {allTabSelected && (
                      <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 10 8" fill="none">
                        <path d="M1 4l3 3 5-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                    {someTabSelected && (
                      <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 10 8" fill="none">
                        <path d="M1.5 4h7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                      </svg>
                    )}
                  </span>
                  <span className="inline-flex items-baseline gap-1.5">
                    {t("listings.filter.selectall")}
                    <span className="text-[11px] font-normal text-gray-400 tabular-nums">
                      {activeTabIds.filter((id) => activeDraft.includes(id)).length}/{activeTabIds.length}
                    </span>
                  </span>
                </button>
              )}
              {loading && (
                <p className="text-center text-sm text-gray-400 py-8">{t("listings.location.loading")}</p>
              )}
              {!loading && searching && searchResults.length === 0 && residenceSearchResults.length === 0 && streetHits.length === 0 && (
                <p className="text-center text-sm text-gray-400 py-8">{t("listings.location.empty")}</p>
              )}
              {!loading && !searching && activeTab !== "complex" && tabLocations.length === 0 && (
                <p className="text-center text-sm text-gray-400 py-8">{t("listings.location.empty")}</p>
              )}
              {!loading && searching && (searchResults.length > 0 || residenceSearchResults.length > 0 || streetHits.length > 0) && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1">
                  {searchResults.map(({ loc, groupType }) => (
                    <LocationRow
                      key={`${groupType}-${loc.id}`}
                      loc={loc}
                      checked={draft.includes(loc.id)}
                      onToggle={() => toggleDraft(loc.id)}
                      hint={t(TAB_KEYS[groupType] ?? "listings.location.tab.rayon")}
                    />
                  ))}
                  {residenceSearchResults.map((r) => (
                    <LocationRow
                      key={`complex-${r.cluster_id}`}
                      loc={{ id: r.cluster_id, name: r.name } as LocationNode}
                      checked={complexDraft.includes(r.cluster_id)}
                      onToggle={() => toggleComplexDraft(r.cluster_id)}
                      hint={t("listings.location.tab.complex")}
                    />
                  ))}
                  {streetHits.map((h) => (
                    <LocationRow
                      key={`street-${h.id}`}
                      loc={{ id: String(h.id), name: h.name } as LocationNode}
                      checked={streetDraft.includes(String(h.id))}
                      onToggle={() => toggleStreetDraft(String(h.id))}
                      hint={h.place ? `${t("listings.location.tab.street")} · ${h.place}` : t("listings.location.tab.street")}
                    />
                  ))}
                </div>
              )}
              {!loading && !searching && activeTab === "complex" && (
                residences.length === 0 ? (
                  <p className="text-center text-sm text-gray-400 py-8">{t("listings.location.empty")}</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1">
                    {residences.map((r) => (
                      <LocationRow
                        key={r.cluster_id}
                        loc={{ id: r.cluster_id, name: r.name } as LocationNode}
                        checked={complexDraft.includes(r.cluster_id)}
                        onToggle={() => toggleComplexDraft(r.cluster_id)}
                        hint={r.district ?? undefined}
                      />
                    ))}
                  </div>
                )
              )}
              {!loading && !searching && activeTab === "qesebe" && qesebeSections.length > 0 && (
                <div className="space-y-3">
                  {qesebeSections.map((section) => (
                    <div key={section.rayon || "other"}>
                      {section.rayon && (
                        <div className="px-3 pt-1 pb-1.5 text-[11px] font-semibold text-gray-400">
                          {section.rayon} r.
                        </div>
                      )}
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-1">
                        {section.items.map((loc) => (
                          <LocationRow
                            key={loc.id}
                            loc={loc}
                            checked={draft.includes(loc.id)}
                            onToggle={() => toggleDraft(loc.id)}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {!loading && !searching && activeTab !== "qesebe" && tabLocations.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-1">
                  {tabLocations.map((loc) => (
                    <LocationRow
                      key={loc.id}
                      loc={loc}
                      checked={draft.includes(loc.id)}
                      onToggle={() => toggleDraft(loc.id)}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Footer: reset + apply (with draft count). Safe-area padding so
                the buttons clear the iOS home indicator now that the sheet
                sits above the mobile nav bar (z-[1200]). */}
            <div className="flex items-center gap-3 px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] sm:pb-3 border-t border-gray-100 shrink-0">
              {/* "Sıfırlayın" on the left, like the filter sheet's bar. */}
              <button
                type="button"
                onClick={resetDraft}
                disabled={draftCount === 0 && !radiusOnly && !drawnOnly}
                className="shrink-0 rounded-xl border border-gray-200 px-3.5 py-2.5 text-sm font-semibold text-gray-700 transition-colors hover:border-gray-300 hover:text-gray-900 disabled:pointer-events-none disabled:text-gray-300 sm:mr-auto"
              >
                {t("listings.filter.reset")}
              </button>
              <button
                type="button"
                onClick={applyAndClose}
                className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-semibold tabular-nums text-white hover:bg-indigo-700 active:bg-indigo-800 transition-colors sm:flex-none"
              >
                {countBase ? showLabel(t, locale, liveCount) : t("listings.location.apply")}
                {!countBase && draftCount > 0 && (
                  <span className="flex items-center justify-center min-w-5 h-5 px-1 rounded-full bg-white/25 text-[11px] font-bold">
                    {draftCount}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
